import {useCallback, useEffect, useState} from 'react'
import styled from 'styled-components'
import type {VariantObject} from 'src/components/products/ProductCard/interfaces'
import {PSmall} from 'src/components/typography'
import {QtyPicker} from 'src/components/form'
import {useCheckout} from 'src/context/CheckoutContext'
import {useDebouncedCallback} from 'use-debounce'
import {useRecoilState} from 'recoil'
import type {Maybe} from 'src/utils/Maybe'
import {CheckoutEventChange} from 'src/context/CheckoutContext/checkout-event'
import {CurrentQuantityItem} from './store'

type Item = VariantObject & {quantity: number}
export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
`

const ButtonLabel = styled(PSmall)`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
`

const DEBOUNCE_TIME_MS = 800

export const CheckoutLineVariant = ({
  item,
  qty,
  variantId,
}: {
  item: Item
  qty: Maybe<number>
  variantId: string
}): JSX.Element => {
  const {cartUpdate} = useCheckout()
  const [currentQty, setCurrentQty] = useState(item.quantity)
  const [_, setAllQuantity] = useRecoilState(CurrentQuantityItem(variantId))

  useEffect(() => {
    setCurrentQty(item.quantity)
  }, [item.quantity])

  const handleUpdateHelper = useCallback(
    async (value: number) => {
      const base = qty ? qty - item.quantity * item.qty_per_unit : 0
      if (base === 0 && value === 0) {
        return
      }
      await cartUpdate({
        [variantId]: base + value * item.qty_per_unit,
      })
    },
    [cartUpdate, item.qty_per_unit, item.quantity, qty, variantId],
  )
  const handleUpdateHelperDebounced = useDebouncedCallback(
    handleUpdateHelper,
    DEBOUNCE_TIME_MS,
  )

  const handleUpdate = useCallback(
    (value: number) => {
      if (CheckoutEventChange) {
        document.body.dispatchEvent(CheckoutEventChange)
      }
      setCurrentQty(value)
      const base = qty ? qty - item.quantity * item.qty_per_unit : 0
      setAllQuantity(base + value * item.qty_per_unit)
      handleUpdateHelperDebounced(value)
    },
    [
      handleUpdateHelperDebounced,
      item.qty_per_unit,
      item.quantity,
      qty,
      setAllQuantity,
    ],
  )

  const isMin =
    (item.quantity === 1 && qty === item.qty_per_unit) || item.quantity === 0
  return (
    <ButtonWrapper>
      <ButtonLabel color="black60">
        {item.name_singular.charAt(0).toUpperCase() +
          item.name_singular.slice(1)}
        {item.qty_per_unit !== 1 ? (
          <span>({item.qty_per_unit} stykki)</span>
        ) : null}
      </ButtonLabel>
      <QtyPicker
        isMin={isMin}
        qty={currentQty}
        isDisabled={false}
        onUpdate={handleUpdate}
      />{' '}
    </ButtonWrapper>
  )
}
